import React, { useState, useEffect, useRef, useContext } from "react";
import PropTypes from "prop-types";
import { MGrid, MSelect, MSpinner, MCard } from "components/Shared";
import Button from "@mui/material/Button";
import { FILTER_TYPE_NONE, FORMATTER_RIGHT_ALIGN } from "components/Shared/MGrid.tsx";
import {DATA_TYPE_BUTTON, DATA_TYPE_DATE_TIME, DATA_TYPE_NUMBER} from "typeConstants/dataTypes";
import { getPayPeriodList } from "requests/homeowner/fieldReps";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import AddEditAdminTimeLogForm from "./AddEditAdminTimeLogForm.tsx";
import { getInspectorAdminTimeLog, addInspectorAdminTimeLog, editInspectorAdminTimeLog, deleteInspectorAdminTimeLog } from "requests/inspectorAdminWorkLogs";
import { getEmployeeAdminTimeLog, addEmployeeAdminTimeLog, deleteEmployeeAdminTimeLog, updateEmployeeAdminTimeLog } from "requests/employeeAdminTimeLogs";
import { defaultMaxHeightWithOverflow } from "styles/sharedStyles";
import { filterOnFilterDate } from "components/Homeowner/History/DataFormatters";

const AdminTimeLog = props => {
  const [payPeriods, setPayPeriods] = useState(null);
  const [currentPayPeriodId, setCurrentPayPeriodId] = useState(null);
  const [currentAdminTimeLog, setCurrentAdminTimeLog] = useState([]);

  let mounted = useRef(true);
  const dialog = useContext(DialogContext);

  useEffect(() => {
    getPayPeriodList(setInitialPayPeriod, mounted);
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (currentPayPeriodId && (props.inspectorNumber || props.employeeId)) {
      getCurrentTimeLog();
    }
  }, [currentPayPeriodId, props.inspectorNumber, props.employeeId]);

  const setInitialPayPeriod = (response) => {
    setPayPeriods(response);
    const payPeriodId = props.payPeriodId || response[0].id;
    setCurrentPayPeriodId(payPeriodId);
  };

  const getCurrentTimeLog = () => {
    if (props.inspectorNumber){
      getInspectorAdminTimeLog(props.inspectorNumber, currentPayPeriodId, setCurrentAdminTimeLog, mounted);
    }
    if (props.employeeId){
      getEmployeeAdminTimeLog(props.employeeId, currentPayPeriodId, setCurrentAdminTimeLog, mounted);
    }
  };

  const getEmptyWorkItem = () => {
    let workItem = {
      workDate: null,
      timeMinutes: null,
      workType: null,
      note: null,
    };
    if (props.inspectorNumber){
      workItem.milesDriven = null;
    }
    return workItem;
  };

  const getColumnDefinitions = () => {
    let columns = [
      { key: "startDateTimeLocal", name: "Start Date", width: 130, dataSource: "startDateTimeLocal", filterType: FILTER_TYPE_NONE, dataType: DATA_TYPE_DATE_TIME },
      { key: "endDateTimeLocal", name: "End Date", width: 130, dataSource: "endDateTimeLocal", filterType: FILTER_TYPE_NONE, dataType: DATA_TYPE_DATE_TIME },
      { key: "workMinutes", name: "Minutes", width: 70, dataSource: "timeMinutes", filterType: FILTER_TYPE_NONE, dataType: DATA_TYPE_NUMBER, formatter: FORMATTER_RIGHT_ALIGN },
    ];
    if (props.inspectorNumber){
      columns.push(
        { key: "workMiles", name: "Miles", width: 70, dataSource: "milesDriven", filterType: FILTER_TYPE_NONE, dataType: DATA_TYPE_NUMBER, formatter: FORMATTER_RIGHT_ALIGN }
      );
    }
    columns.push(
      { key: "workType", name: "Work Type", dataSource: "workTypeName", filterType: FILTER_TYPE_NONE },
      { key: "workNote", name: "Note", dataSource: "note", filterType: FILTER_TYPE_NONE },
    );
    if (props.inspectorNumber){
      columns.push(
        { key: "createdByUser", name: "User", dataSource: "createdByUser", filterType: FILTER_TYPE_NONE }
      );
    }
    if (props.canAddAndEdit){
      columns.push(
        { key: "workEdit", name: "Edit", width: 60, dataSource: "editIcon", filterType: FILTER_TYPE_NONE, dataType: DATA_TYPE_BUTTON, tooltip: () => "Edit" },
        { key: "workDelete", name: "Delete", width: 60, dataSource: "deleteIcon", filterType: FILTER_TYPE_NONE, dataType: DATA_TYPE_BUTTON, tooltip: () => "Delete" },
      );
    }
    return columns;
  };

  const handleSave = workItem => {
    let payload = {
      ...workItem,
      workTypeCode: workItem.workType.code,
      timeMinutes: parseInt(workItem.timeMinutes),
    };
    if (props.inspectorNumber){
      payload.inspectorNumber = props.inspectorNumber;
      payload.milesDriven = parseInt(workItem.milesDriven);
      if (workItem.id){
        editInspectorAdminTimeLog(payload, getCurrentTimeLog, mounted);
      } else {
        addInspectorAdminTimeLog(payload, getCurrentTimeLog, mounted);
      }
    }
    if (props.employeeId){
      payload.employeeId = props.employeeId;
      if (workItem.id){
        updateEmployeeAdminTimeLog(payload, getCurrentTimeLog, mounted);
      } else {
        addEmployeeAdminTimeLog(payload, getCurrentTimeLog, mounted);
      }
    }
    dialog.hide();
  };

  const onDelete = workItemId => {
    const payload = {
      id: workItemId
    };
    if (props.inspectorNumber){
      deleteInspectorAdminTimeLog(payload, getCurrentTimeLog, mounted);
    }
    if (props.employeeId){
      deleteEmployeeAdminTimeLog(workItemId, getCurrentTimeLog, mounted);
    }
    dialog.hide();
  };

  const onPayPeriodChange = (option) => {
    setCurrentPayPeriodId(option.value);
  };

  let dialogProps = { className: "admin-time-log-dialog" };

  const onAddClick = () => {
    let emptyWorkItem = getEmptyWorkItem();
    dialog.show(null,
      <AddEditAdminTimeLogForm
        title="Add Admin Time Log"
        currentWorkItem={emptyWorkItem}
        minPayPeriodDate={getMinDate()}
        MaxPayPeriodDate={getMaxDate()}
        onSave={handleSave}
        onCancel={dialog.hide}
        inspectorNumber={props.inspectorNumber}
      />, null, dialogProps);
  };

  const handleGridClick = (row, column, gridRows) => {
    if (!props.canAddAndEdit) return;
    const workItem = gridRows[row];

    switch (column) {
      case "workDelete":
        dialog.show(
          "Delete Admin Time Log",
          `Are you sure you want to delete the entry for ${workItem.timeMinutes} minutes on ${getFormattedDate(workItem.workDate)}`,
          [{
            key: "admin-timelog-delete-confirm-button",
            label: "Yes, Delete",
            isPrimary: true,
            onClick: () => onDelete(workItem.id)
          },
          {
            key: "admin-timelog-delete-cancel",
            label: "Cancel",
            onClick: () => dialog.hide()
          }]);
        break;
      case "workEdit":
        dialog.show(null,
          <AddEditAdminTimeLogForm
            title="Edit Admin Time Log"
            currentWorkItem={workItem}
            minPayPeriodDate={getMinDate()}
            MaxPayPeriodDate={getMaxDate()}
            onSave={handleSave}
            onCancel={dialog.hide}
            inspectorNumber={props.inspectorNumber}
          />, null, dialogProps);
        break;
    }
  };

  const getMinDate = () => {
    const payPeriod = payPeriods.find(x => x.id === currentPayPeriodId);
    if (!payPeriod) return;
    return new Date(payPeriod.fromDate.year, payPeriod.fromDate.month - 1, payPeriod.fromDate.day);    // month is an 0-based index
  };

  const getMaxDate = () => {
    const payPeriod = payPeriods.find(x => x.id === currentPayPeriodId);
    if (!payPeriod) return;
    const currentDate = new Date();
    if (payPeriod.toDate.day > currentDate.getDate() || payPeriod.toDate.month > currentDate.getMonth() + 1) {
      return new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    } else {
      return new Date(payPeriod.toDate.year, payPeriod.toDate.month - 1, payPeriod.toDate.day);     // month is an 0-based index
    }
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return;
    return `${dateString.slice(5, 7)}/${dateString.slice(8, 10)}/${dateString.slice(0, 4)}`;
  };

  const getGridData = (rows) => rows.map(x => ({
        workTypeName: x.workType.name,
        ...x,
        editIcon: editIcon(),
        deleteIcon: deleteIcon(),
      }));

  const editIcon = () => <i className="md-icon material-icons">edit</i>;
  const deleteIcon = () => <i className="md-icon material-icons">delete</i>;

  if (payPeriods === null) return <MSpinner />;
  const filteredAdminTimeLog = currentAdminTimeLog.filter((row) =>
    filterOnFilterDate(props.filterDate, row.startDateTimeLocal) || filterOnFilterDate(props.filterDate, row.endDateTimeLocal)
  );
  const gridData = getGridData(filteredAdminTimeLog);

  return (
    <MCard className="card-unnested profile-card admin-timelog-card" title="Training and Misc Log">
      <div className="admin-timelog-content-container">
        <div className="admin-timelog-pay-period-container">
          {!props.payPeriodId &&
            <MSelect
                id="admintime-mselect"
                className="admin-timelog-pay-period-dropdown"
                isClearable={false}
                onChange={onPayPeriodChange}
                options={payPeriods.map(x => ({
                  value: x.id,
                  label: x.fromToDisplay
                }))}
                defaultValue={payPeriods[0].fromToDisplay}
            />}
          {payPeriods && currentPayPeriodId && payPeriods.filter(x => x.id === currentPayPeriodId)[0].closedDate === null
            && props.canAddAndEdit &&
            <Button variant="contained" onClick={onAddClick}>Add</Button>}
        </div>
        <div className="admin-timelog-grid-container">
          {gridData && gridData.length > 0
            ? <MGrid
              columnDefinitions={getColumnDefinitions()}
              onCellButtonClick={(row, column) => handleGridClick(row, column, filteredAdminTimeLog)}
              dataLastChanged={Date.now()}
              wrapperStyle={props.gridWrapperStyle}
              data={gridData} />
            : <p className="admin-timelog-message">No admin time has been logged for this pay period.</p>
          }
        </div>
      </div>
    </MCard>
  );
};

AdminTimeLog.propTypes = {
  payPeriodId: PropTypes.number,  // Optional, if provided, will only display for a single pay period without the dropdown to select others
  canAddAndEdit: PropTypes.bool,  // User can add or edit admin time logs
  employeeId: PropTypes.number, // Provide this for any user type except FRs
  inspectorNumber: PropTypes.number,  // Provide this for FRs only
  gridWrapperStyle: PropTypes.object, // Optional styles for the MGrid wrapper - defaults to { maxHeight: 400, overflow: "auto" }
  filterDate: PropTypes.instanceOf(Date),
};

AdminTimeLog.defaultProps = {
  gridWrapperStyle: defaultMaxHeightWithOverflow,
};

export default AdminTimeLog;
